/**
 * Resolve the founder's REAL `startup_profiles.id` for the scoring interview.
 *
 * Ownership moved from `startup_profiles.user_id` (legacy single-owner) to
 * `team_members` (owner/member) per the 2026-07-09 onboarding redesign. The
 * onboarding create+claim path writes a `team_members` owner row and flips
 * `startup_profiles.is_claimed` — it deliberately does NOT set
 * `startup_profiles.user_id`. The previous implementation here looked ONLY at
 * `startup_profiles.user_id`, so a founder who had just created + claimed their
 * company resolved to `null` and was stranded on the interview's "Complete Your
 * Profile" guard even though the company existed and they owned it.
 *
 * The fix is to resolve exactly the way the rest of the app now does — via the
 * canonical `resolveMyStartup` seam (team_members-first, legacy `user_id`
 * fallback for pre-migration rows). This hook stays as the interview's entry
 * point (it owns the mock short-circuit and the retry query key), but the
 * resolution logic is no longer duplicated here.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase, isSupabaseConfigured } from '@/lib/supabase/client';
import { mode } from '@/lib/config/app-mode';
import { handleError } from '@/lib/errors';
import { resolveMyStartup } from '@/lib/startup/use-my-startup';

export const founderStartupKeys = {
  mine: ['interview', 'founder-startup'] as const,
};

/**
 * Read the authenticated founder's real startup id via the team_members-aware
 * resolver. Returns `null` (a valid, terminal state) when the caller owns no
 * startup yet; throws on a query error so the caller can retry. Mock /
 * unconfigured environments have no real DB row to resolve, so they short to
 * `null` before touching the client.
 */
export async function fetchFounderStartupId(): Promise<string | null> {
  if (mode.isMock || !isSupabaseConfigured()) return null;
  const { startupId } = await resolveMyStartup(supabase);
  return startupId;
}

export interface FounderStartup {
  startupId: string | null;
  isLoading: boolean;
  error: unknown;
}

/**
 * Resolve the founder's real startup id for the interview, independent of the
 * (possibly simulated) Zustand profile. `startupId` is `null` both while loading
 * and when the founder genuinely has no startup — always read `isLoading` to
 * tell the two apart before treating `null` as "no startup".
 */
export function useFounderStartup(): FounderStartup {
  const query = useQuery({
    queryKey: founderStartupKeys.mine,
    queryFn: async (): Promise<string | null> => {
      try {
        return await fetchFounderStartupId();
      } catch (err) {
        handleError(err, { context: 'useFounderStartup' });
        throw err;
      }
    },
  });

  return {
    startupId: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
  };
}
